/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { GetStaticPaths, GetStaticProps, NextLayoutComponentType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import MainLayout from '@/layouts/main';
import { localeList } from '@/public/locales/locale-list';
import AppContainer from '@/components/layout/container';
import { Api } from '@/api';
import s from './styles.module.scss';
import { IReadSurah } from '@/infrastructure/interfaces/i-read-surah';
import {
  ReadSurah,
  readSurahListFields,
} from '@/infrastructure/class/read-surah';
import { IBreadcrumb } from '@/components/common/breadcrumbs';
import { routes } from '@/infrastructure/const/routes';
import { useRouter } from 'next/router';
import { IAyat } from '@/infrastructure/interfaces/i-ayat';
import ReadSurahDetailHeader from '@/components/read-surahs/detail/header';
import AyasList from '@/components/ayas/list';
import { ITranslation } from '@/infrastructure/interfaces/i-translation';
import { allRecordsPaginationOptions } from '@/infrastructure/const/pagination';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import { ReciterAudioSurah } from '@/infrastructure/class/reciter-audio-surah';
import { useAuth } from '@/infrastructure/context/auth-context';
import { Ayat } from '@/infrastructure/class/ayat';
import {
  BreadcrumbContext,
  IBreadcrumbContextType,
} from '@/infrastructure/context/breadcrumb-context';
import {
  baseMarkupSchema,
  breadcrumbListMarkupSchema,
  organizationMarkupSchema,
  webPageMarkupSchema,
  webSiteMarkupSchema,
} from '@/infrastructure/markup-schemas';
import SeoHead from '@/components/common/seo-head';
import { replaceWords } from '@/utils/helpers/replace-words';
import { getLocaleForUrl } from '@/infrastructure/functions/get-locale-for-url';
import { useReadBookmarks } from '@/infrastructure/context/read-bookmarks-context';
import { decryptPageProps } from '@/utils/crypto/page-props-decrypt';
import { IEncryptedProps } from '@/infrastructure/interfaces/i-encrypted-props';
import { addDomainForReciterImages } from '@/utils/helpers/add-domain-for-reciter-images';

const api = Api.getInstance();
const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL;

const isValidSlug = (slug: unknown): slug is string =>
  typeof slug === 'string' && slug.trim().length > 0;

interface ISurahReadDetailProps {
  readSurah: IReadSurah;
  readSurahs: IReadSurah[];
  audioSurahs: IAudioSurah[];
  ayas: IAyat[];
  translations: ITranslation[];
  translation: string;
}

const SurahReadDetail: NextLayoutComponentType<IEncryptedProps> = (props) => {
  const { data } = props;
  const decrypted = decryptPageProps<ISurahReadDetailProps>(data);
  const {
    readSurah,
    readSurahs,
    audioSurahs,
    ayas,
    translations,
    translation,
  } = decrypted;
  const { saveLastRead } = useReadBookmarks();

  const { authUser } = useAuth();

  const { setBreadcrumbs } = useContext(
    BreadcrumbContext
  ) as IBreadcrumbContextType;

  const router = useRouter();
  const { locale } = router;
  const activeTranslation = Array.isArray(router.query.translation)
    ? router.query.translation[0]
    : (router.query.translation ?? null);

  const { t: tMeta } = useTranslation('meta');
  const { t: tLinks } = useTranslation('links');

  const [lastReadedAyat, setLastReadedAyat] = useState<number>(1);
  const [readSurahDetail, setReadSurahDetail] = useState<ReadSurah>(
    new ReadSurah(readSurah, locale)
  );
  const [displayedAyas, setDisplayedAyas] = useState<IAyat[]>(ayas);

  useEffect(() => {
    if (!activeTranslation) {
      return;
    }

    (async () => {
      const { data: ayasResult } = await api.getAyasBySuraId(
        String(readSurah.id),
        activeTranslation
      );
      setDisplayedAyas(ayasResult.ayas);
    })();
  }, [activeTranslation]);

  const breadcrumbs: IBreadcrumb[] = [
    {
      title: tLinks('home'),
      path: routes.home,
    },
    {
      title: tLinks('surahs'),
      path: routes.surahRead,
    },
    {
      title: readSurahDetail.titleEn,
      path: `${routes.surahRead}/${readSurahDetail.slug}`,
    },
  ];

  const canonicalURL =
    `${SITE_URL}${getLocaleForUrl(locale)}` +
    `${routes.surahRead}/${readSurahDetail.slug}`;

  const htmlTitle = (): string => {
    return replaceWords(`${tMeta('surahsDetailPageTitle')}`, {
      transliteration: readSurahDetail.transliteration,
      ayasCount: String(ayas.length),
    });
  };
  const htmlDescription = (): string => {
    return replaceWords(`${tMeta('surahsDetailPageDescription')}`, {
      transliteration: readSurahDetail.transliteration,
      ayasCount: String(ayas.length),
      title: readSurahDetail.titleEn,
    });
  };

  const shema = baseMarkupSchema([
    webPageMarkupSchema(
      `${routes.surahRead}/${readSurahDetail.slug}`,
      htmlTitle(),
      htmlDescription()
    ),
    webSiteMarkupSchema(),
    organizationMarkupSchema(),
    breadcrumbListMarkupSchema(breadcrumbs),
  ]);

  const surahs = useMemo<ReadSurah[]>(
    () =>
      sortArrayByFieldName(
        readSurahs.map((readSurah) => new ReadSurah(readSurah, locale)),
        'index'
      ),
    [readSurahs, locale]
  );

  const reciterAudioSurahs = useMemo<ReciterAudioSurah[]>(
    () =>
      sortArrayByFieldName(
        audioSurahs.map(
          (audioSurah) => new ReciterAudioSurah(audioSurah.reciter!, audioSurah)
        ),
        'reciter'
      ),
    [audioSurahs]
  );

  const onAyatView = (ayat: Ayat) => {
    setLastReadedAyat(ayat.number);
  };

  useEffect(() => {
    setReadSurahDetail(new ReadSurah(readSurah, locale));
  }, [data, locale]);

  useEffect(
    () => () => {
      if (authUser) {
        (async () => {
          await saveLastRead({
            user: authUser,
            readSurah: readSurahDetail,
            ayaNumber: lastReadedAyat,
          });
        })();
      }
    },
    [authUser, lastReadedAyat, readSurahDetail]
  );

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, []);

  return (
    <>
      <SeoHead
        title={htmlTitle()}
        description={htmlDescription()}
        canonicalURL={canonicalURL}
        schema={shema}
      />
      <main>
        <div className={s['detail-header-wrapper']}>
          <ReadSurahDetailHeader
            readSurah={readSurahDetail}
            readSurahs={surahs}
            reciters={reciterAudioSurahs}
            translations={translations}
            translation={activeTranslation ?? translation}
            ayasCount={ayas.length}
            className={s['detail-header']}
            withDetail
          />
        </div>
        <AppContainer>
          <AyasList
            items={displayedAyas}
            surah={readSurahDetail}
            onAyatView={onAyatView}
          />
        </AppContainer>
      </main>
    </>
  );
};

SurahReadDetail.getLayout = (page) => <MainLayout>{page}</MainLayout>;

export const getStaticPaths: GetStaticPaths = async () => {
  const { data: readSurahsResult } = await api.getReadSurahs(
    allRecordsPaginationOptions,
    ['slug']
  );
  const { results: readSurahs } = readSurahsResult;

  return {
    paths: readSurahs
      .map((surah: { slug: unknown }) => surah.slug)
      .filter(isValidSlug)
      .map((slug: string) => ({
        params: { slug },
      })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const { locale = '', params } = ctx;
    const { slug } = params || {};

    const { data: translationsResult } = await api.getAllTranslations();
    const { translations } = translationsResult;

    const translationSlug = '';

    const { data } = await api.getReadSurahBySlug(String(slug));
    const { surah: readSurah } = data;

    const { data: ayasResult } = await api.getAyasBySuraId(
      readSurah.id,
      translationSlug
    );
    const { ayas } = ayasResult;

    const { data: readSurahsResult } = await api.getReadSurahs(
      allRecordsPaginationOptions,
      readSurahListFields
    );
    const { results: readSurahs } = readSurahsResult;

    const { data: audioSurahsResult } = await api.getAudioSurahsBySurahNumber(
      readSurah.index
    );
    const { results: audioSurahs } = audioSurahsResult;

    audioSurahs?.map((audioSurah: IAudioSurah) => {
      if (audioSurah.reciter) {
        addDomainForReciterImages(audioSurah.reciter);
      }
    });

    const dataToEncrypt = {
      readSurah,
      readSurahs,
      ayas,
      translations,
      translation: translationSlug,
      audioSurahs,
    };
    const { encryptPageProps } =
      await import('@/utils/crypto/page-props-encrypt');
    const encrypted = encryptPageProps(dataToEncrypt);

    return {
      props: {
        data: encrypted,
        ...(await serverSideTranslations(locale, localeList)),
      },
    };
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
};

export default SurahReadDetail;
